import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);  // Inicializar como null, no desde localStorage
  const [loading, setLoading] = useState(true);
  const [ssoError, setSsoError] = useState(null);

  // Verificar token al cargar - y manejar errores de OAuth
  useEffect(() => {
    const verifyToken = async () => {
      // Verificar si hay error de OAuth guardado
      const oauthError = localStorage.getItem('oauth_error');
      if (oauthError) {
        setSsoError(oauthError);
        localStorage.removeItem('oauth_error');
      }
      
      // CRITICAL: Si hay token o error en el hash (callback de Google), AppWithAuth lo manejará
      const hash = window.location.hash;
      if (hash?.includes('token=') || hash?.includes('error=')) {
        console.log('[AuthContext] Detected Google OAuth callback, skipping /me check');
        setLoading(false);
        return;
      }
      
      const storedToken = localStorage.getItem('auth_token');
      console.log('[AuthContext] Verificando token al cargar, existe:', !!storedToken);
      
      // Si no hay token guardado, limpiar estado y mostrar login
      if (!storedToken) {
        console.log('[AuthContext] No hay token guardado, mostrando login');
        setUser(null);
        setToken(null);
        setLoading(false);
        return;
      }
      
      try {
        console.log('[AuthContext] Verificando token con /api/auth/me');
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${storedToken}` },
          cache: 'no-store'
        });
        if (res.ok) {
          const resClone = res.clone();
          let userData;
          try {
            userData = await res.json();
          } catch {
            userData = await resClone.json();
          }
          console.log('[AuthContext] Token válido, usuario:', userData?.email);
          setUser(userData);
          setToken(storedToken);
        } else {
          // Token inválido
          console.log('[AuthContext] Token inválido, limpiando localStorage');
          localStorage.removeItem('auth_token');
          setToken(null);
          setUser(null);
        }
      } catch (err) {
        console.error('[AuthContext] Error verifying token:', err);
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };
    verifyToken();
  }, []);

  const login = async (email, password) => {
    console.log('[AuthContext] Iniciando login para:', email);
    
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      cache: 'no-store'  // Prevent caching issues
    });
    
    let data;
    try {
      data = await res.json();
      console.log('[AuthContext] Respuesta del servidor:', { success: data.success, hasToken: !!data.token, hasUser: !!data.user });
    } catch (jsonError) {
      console.error('[AuthContext] JSON parse failed:', jsonError);
      throw new Error('Error al procesar respuesta del servidor');
    }
    
    if (!res.ok) {
      console.error('[AuthContext] Login failed:', data.detail);
      throw new Error(data.detail || 'Error de autenticación');
    }
    
    // Guardar token en localStorage
    if (data.token) {
      localStorage.setItem('auth_token', data.token);
      console.log('[AuthContext] Token guardado en localStorage');
    } else {
      console.error('[AuthContext] No se recibió token del servidor');
      throw new Error('No se recibió token del servidor');
    }
    
    setToken(data.token);
    setUser(data.user);
    
    console.log('[AuthContext] Login completado, user:', data.user?.email);
    
    return data;
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } catch (err) {
      console.error('Error during logout:', err);
    }
    
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
  };

  const changePassword = async (currentPassword, newPassword) => {
    const res = await fetch(`${API_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword
      }),
      cache: 'no-store'
    });
    
    let data;
    try {
      data = await res.json();
    } catch (e) {
      throw new Error('Error al procesar respuesta del servidor');
    }
    
    if (!res.ok) {
      throw new Error(data.detail || 'Error al cambiar contraseña');
    }
    
    // Hacer logout para forzar nuevo login con nueva contraseña
    localStorage.removeItem('auth_token');
    // Guardar flag para mostrar mensaje en login
    localStorage.setItem('password_changed', 'true');
    setToken(null);
    setUser(null);
    
    // Forzar redirect a login después de un breve delay
    setTimeout(() => {
      window.location.href = '/';
    }, 100);
    
    return data;
  };

  const skipPasswordChange = async () => {
    const res = await fetch(`${API_URL}/api/auth/skip-password-change`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      cache: 'no-store'
    });
    
    let data;
    try {
      data = await res.json();
    } catch (e) {
      throw new Error('Error al procesar respuesta del servidor');
    }
    
    if (!res.ok) {
      throw new Error(data.detail || 'Error al omitir cambio de contraseña');
    }
    
    // Actualizar el usuario con must_change_password = false
    setUser(data.user);
    
    return data;
  };

  // ============================================================
  // GOOGLE OAUTH DIRECTO - App OAuth propia de Capabilia
  // ============================================================
  // El flujo es server-side: el backend maneja todo el OAuth
  // Frontend solo redirige a /api/auth/google/login
  // ============================================================
  const loginWithGoogle = () => {
    console.log('[AuthContext] Initiating Google OAuth...');
    setSsoError(null);
    
    // Redirigir al endpoint del backend que inicia el flujo OAuth
    // El backend redirigirá a Google y manejará el callback
    window.location.href = `${API_URL}/api/auth/google/login`;
  };

  // Limpiar error de SSO
  const clearSsoError = () => setSsoError(null);

  // Helpers para permisos
  const canUploadData = user?.permissions?.can_upload_data || false;
  const canManageUsers = user?.permissions?.can_manage_users || false;
  const canViewAllPartners = user?.permissions?.can_view_all_partners || false;
  const isReadOnly = user?.permissions?.is_readonly || false;

  const value = {
    user,
    token,
    loading,
    login,
    loginWithGoogle,
    logout,
    changePassword,
    skipPasswordChange,
    isAuthenticated: !!user,
    canUploadData,
    canManageUsers,
    canViewAllPartners,
    isReadOnly,
    ssoError,
    clearSsoError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
